/*
 * TunePad
 *
 * Michael S. Horn
 * Northwestern University
 * michael-horn@northwestern.edu
 *
 * This project was funded by the National Science Foundation (grant DRL-1612619).
 * Any opinions, findings and conclusions or recommendations expressed in this
 * material are those of the author(s) and do not necessarily reflect the views
 * of the National Science Foundation (NSF).
 */
import html from './project-menu.module.html';
import styles from './project-menu.module.css' with { type: 'css' }
import { TunePadAudio, MusicKey } from '../audio';
import { showBPMDialog } from './dialogs/bpm-dialog';
import { showTimeDialog } from './dialogs/time-dialog';
import { showKeyDialog } from './dialogs/key-dialog';

/**
 * ## TunePad project menu bar
 * ```html
 * <tunepad-project-menu name="My Project" readonly="false"></tunepad-project-menu>
 * ```
 */
export class TunePadProjectMenu extends HTMLElement {

    static readonly ELEMENT = "tunepad-project-menu";

    static observedAttributes = [ "name", "readonly", "profile-image" ];

    /** TunePad audio system */
    private audio : TunePadAudio;

    /** all of the HTML elements are contained within a shadow DOM */
    private root : ShadowRoot;
    
    /** name input field */
    private nameField : HTMLInputElement;

    /** internal state for tempo that gets externalized to the audio system via custom event */
    private bpm : number;

    /** internal state for time signature */
    private time : string;

    /** internal state for key signature */
    private key : MusicKey;

    /** readonly status */
    private get readonly() : boolean { return this.getAttribute('readonly') === 'true'; }

    private playButton : HTMLButtonElement;
    private pauseButton : HTMLButtonElement;
    private stopButton : HTMLButtonElement;
    private settingsButton : HTMLButtonElement;


    constructor() {
        super();
        this.root = this.attachShadow({ mode: 'open' });
        this.root.adoptedStyleSheets.push(styles);
        this.root.innerHTML = html;
        this.nameField = this.root.querySelector('#project-name') as HTMLInputElement;
        this.audio = TunePadAudio.init();
        this.audio.addSubscriber(this);
        this.bpm = this.audio.bpm;
        this.time = `${this.audio.beatsPerMeasure}/${this.audio.beatValue}`;
        this.key = new MusicKey(this.audio.key.name); 

        this.playButton = this.root.querySelector('#play-button') as HTMLButtonElement;
        this.pauseButton = this.root.querySelector('#pause-button') as HTMLButtonElement;
        this.stopButton = this.root.querySelector('#stop-button') as HTMLButtonElement;
        this.settingsButton = this.root.querySelector('#settings') as HTMLButtonElement;
    }


    async connectedCallback() {
        await this.waitForWindowLoad();
        if (this.parentElement?.nodeName.toLowerCase() === 'tunepad-project') {
            this.setName(this.parentElement.getAttribute('name') ?? '');
        }

        this.setTempo(this.audio.bpm);
        this.setTimeSignature(this.audio.beatsPerMeasure, this.audio.beatValue);
        this.setKey(this.audio.key.name);

        this.nameField.addEventListener('blur', (e) => {
            if (this.readonly) return;
            const oldName = this.parentElement?.getAttribute('name') ?? '';
            const newName = this.nameField.value;
            if (newName.length > 0) {
                this.setName(newName);
                this.emitUpdateEvent('name', this.nameField.value);
            } else {
                this.nameField.value = oldName;
            }
        });

        this.nameField.addEventListener('keypress', (e) => {
            if (this.readonly) return;
            if (e.key === "Enter") {
                this.nameField.blur();
            }
        });

        this.root.querySelector('#home-button')?.addEventListener('click', e => {
            this.emitButtonEvent('home');
        });
        this.root.querySelector('#share-button')?.addEventListener('click', e => {
            this.emitButtonEvent('share');
        });
        this.root.querySelector('#help-button')?.addEventListener('click', e => {
            this.emitButtonEvent('help');
        });
        this.root.querySelector('#profile-button')?.addEventListener('click', e => {
            this.emitButtonEvent('profile');
        });

        this.playButton.addEventListener('click', e => {
            this.pauseButton.classList.remove('hidden');
            this.playButton.classList.add('hidden');
            this.emitButtonEvent('play');
        });
        this.pauseButton.addEventListener('click', e => {
            this.pauseButton.classList.add('hidden');
            this.playButton.classList.remove('hidden');
            this.emitButtonEvent('pause');
        });
        this.stopButton.addEventListener('click', e => {
            this.pauseButton.classList.add('hidden');
            this.playButton.classList.remove('hidden');
            this.emitButtonEvent('stop');
        });
        this.settingsButton.addEventListener('click', e => {
            this.root.querySelector('.music-settings')?.classList.toggle('show');
        });
        this.bindDialogEvents();
    }


    disconnectedCallback() {
        this.audio.removeSubscriber(this);
    }


    attributeChangedCallback(name : string, oldValue : string, newValue : string) {
        if (name === 'name') {
            this.setName(newValue);
        }
        else if (name === 'readonly' && oldValue !== newValue) {
            this.setReadOnly(newValue === 'true');
        }
        else if (name === 'profile-image' && oldValue !== newValue) {
            this.setProfileImage(newValue);
        }
    }

    onClockReset() {
        this.playButton.classList.remove('hidden');
        this.pauseButton.classList.add('hidden');
    }
    onClockTimeChange() {
        this.playButton.classList.remove('hidden');
        this.pauseButton.classList.add('hidden');
    }
    onTempoChange() {
        this.setTempo(this.audio.bpm);
    }
    onKeyChange() {
        this.setKey(this.audio.key.name);
    }
    onTimeSignatureChange() {
        this.setTimeSignature(this.audio.beatsPerMeasure, this.audio.beatValue);
    }

    onPlayStart() {
        this.playButton.classList.add('hidden');
        this.pauseButton.classList.remove('hidden');
    }

    onAllPause() {
        this.playButton.classList.remove('hidden');
        this.pauseButton.classList.add('hidden');
    }

    private setProfileImage(src : string) {
        const button = this.root.querySelector('#profile-button');
        if (button) {
            button.innerHTML = `<img src="${src}" alt="Profile Picture" class="profile-image"></img>`;
        }
    }

    private setName(name : string) {
        if (name === this.nameField.value) return;
        this.nameField.value = name;
        this.setAttribute('name', name);
    }

    private setReadOnly(ro : boolean) {
        if (this.readonly !== ro) {
            this.setAttribute('readonly', `${ro}`);
            this.nameField.readOnly = ro;
        }
    }

    private setTempo(newBPM : number) {
        this.bpm = Math.max(5, Math.min(999, newBPM));
        const bpmLabel = this.root.querySelector('#bpm');
        if (bpmLabel) bpmLabel.innerHTML = `${this.bpm}`;
    }

    private setTimeSignature(numer : number, denom : number) {
        numer = Math.max(1, Math.min(32, numer));
        denom = [1, 2, 4, 8, 16, 32, 64].includes(denom) ? denom : 4;
        this.time = `${numer}/${denom}`;
        this.setHTML('#time-signature', this.time);
    }

    private setKey(name : string) {
        this.key = new MusicKey(name);
        this.setHTML('#key-base', `${this.key.key.split('/')[0]}`);
        this.setHTML('#key-mode', `${this.key.mode}`);
    }

    private emitUpdateEvent(property : string, value : any) {
        this.dispatchEvent(new CustomEvent("update", {
            bubbles: false,
            composed: true,
            detail: { property, value }
        }));
    }

    private emitButtonEvent(name : string) {
        this.dispatchEvent(new CustomEvent("action", {
            bubbles: false,
            composed: true,
            detail: { 'action' : name }
        }));
    }


    private setHTML(selector : string, value : string) {
        const e = this.root.querySelector(selector);
        if (e) e.innerHTML = value;
    }

    private async waitForWindowLoad() : Promise<void> {
        return new Promise((resolve) => {
            if (document.readyState === 'complete') {
                resolve();
            } else {
                window.addEventListener('load', () => {
                    resolve();
                });
            }
        });
    }

    private bindDialogEvents() {

        this.root.querySelector('#change-bpm')?.addEventListener('click', e => {
            if (this.readonly) return;
            showBPMDialog(this.bpm, (newTempo) => {
                this.setTempo(newTempo);
                this.emitUpdateEvent('tempo', `${this.bpm}` );
            });
            this.root.querySelector('.music-settings')?.classList.remove('show');
        });

        this.root.querySelector('#change-time')?.addEventListener('click', e => {
            if (this.readonly) return;
            showTimeDialog(this.audio.beatsPerMeasure, this.audio.beatValue, (n, d) => {
                this.setTimeSignature(n, d);
                this.emitUpdateEvent('time', `${n}/${d}`);
            });
            this.root.querySelector('.music-settings')?.classList.remove('show');
        });

        this.root.querySelector('#change-key')?.addEventListener('click', e => {
            if (this.readonly) return;
            showKeyDialog(this.key.name, (key) => {
                this.setKey(key);
                this.emitUpdateEvent('key', this.key.name);
            });
            this.root.querySelector('.music-settings')?.classList.remove('show');
        });
    }
}

